/**
 * 공개 지면(랜딩·기능·요금)에서 **서비스로 들어가는 길** — 로그인한 사람은 서비스로, 아직 아닌 사람은 비회원 질문 대화나 로그인 화면으로.
 * 순수 함수다 — 단추·검색칸 훅이 인증 상태를 넘겨 부른다.
 * 🔑 랜딩 검색칸 말은 질문이 된다 — 비회원은 비회원 질문 대화(`/ask?q=`), 로그인한 사람은 서비스 홈 대화(`/service?question=`).
 *   질문 길잡이가 꺼져 있으면 비회원은 로그인 화면(돌아올 곳 = 그 검색어의 서비스 홈 검색) · 로그인한 사람은 서비스 홈 검색어.
 *   길이·모양은 `normalizeGuestQuestion` 한 곳이 정한다(서버 페이지도 같은 함수로 다시 거른다).
 */

import { paths } from 'src/routes/paths';

import { ASK_ROUTER_OPEN } from 'src/constants/ai-features';
import { ASK_QUESTION_MAX } from 'src/constants/ask-screens';

import { guestLoginHref } from './guest-gate';

/**
 * 비회원 질문 다듬기 — 앞뒤 공백·제어 문자를 걷고, 겹친 공백을 하나로, `ASK_QUESTION_MAX` 자까지.
 * @param raw 주소의 `q`(배열이면 첫 값) — 문자열이 아니면 빈 말
 */
export function normalizeGuestQuestion(raw: unknown): string {
  const first = Array.isArray(raw) ? raw[0] : raw;
  if (typeof first !== 'string') return '';
  // eslint-disable-next-line no-control-regex
  const text = first.replace(/[\u0000-\u001f\u007f]/g, ' ').replace(/\s+/g, ' ').trim();
  return Array.from(text).slice(0, ASK_QUESTION_MAX).join('').trim();
}

/** 비회원 질문 대화 주소 — 질문이 있으면 `?q=` 로 싣는다(질문 글자만) */
export function guestAskHref(question = ''): string {
  const q = normalizeGuestQuestion(question);
  return q ? `${paths.ask}?${new URLSearchParams({ q }).toString()}` : paths.ask;
}

/**
 * 「무료로 시작하기」류 단추가 가는 곳 — 로그인했으면 서비스(`/service`), 아니면 로그인 화면(간편가입 포함 · 돌아올 곳 = 서비스 홈).
 * @param authenticated 로그인했는가 — 인증이 풀리기 전에는 거짓으로 넘긴다
 */
export function publicTrialHref(authenticated: boolean): string {
  return authenticated ? paths.service.root : guestLoginHref(paths.service.home);
}

/**
 * 랜딩 검색칸이 가는 곳 — 로그인했으면 서비스 홈 대화(`/service?question=` · 한 번 묻고 `?chat=1`), 아니면 비회원 질문 대화(`/ask?q=`).
 * 두 갈래 모두 같은 질문 다듬기(`normalizeGuestQuestion` · `ASK_QUESTION_MAX` 자)를 거친다.
 * 질문 길잡이가 꺼져 있으면(`askOpen` 거짓) 로그인한 사람은 서비스 홈 검색(`/service?q=`), 비회원은 그 검색으로 돌아오는 로그인 화면으로 간다.
 * @returns 빈 말이면 `null`(아무 데도 가지 않는다)
 */
export function heroSearchHref(word: string, authenticated: boolean, askOpen: boolean = ASK_ROUTER_OPEN): string | null {
  const question = normalizeGuestQuestion(word);
  if (!question) return null;
  if (askOpen) return authenticated ? paths.service.findAsk(question) : guestAskHref(question);
  const q = word.trim();
  return authenticated ? paths.service.findQuery(q) : guestLoginHref(paths.service.findQuery(q));
}
